/** SUNTRONIX — Product Intelligence hub (Technology → Product Intelligence).
 *  Surfaces the Lenovo PSREF / OEM connector network to Sales & Admin:
 *  PSREF lookup + import, imported products, OEM updates, connector health. */
import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, Search, CheckCircle2, ExternalLink, FileText, ChevronDown, Loader2,
  AlertTriangle, PackageCheck, RefreshCw, Activity, Cpu,
} from "lucide-react";
import { QuickActionGroup } from "@/components/quickactions/QuickActionGroup";

const renderBold = (text) =>
  (text || "").split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={i} className="text-slate-900">{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>);

const TABS = [
  { id: "lookup", label: "Search" },
  { id: "imported", label: "Product Library" },
  { id: "updates", label: "Updates" },
  { id: "health", label: "Source Diagnostics" },
];
const STATUS_TONE = {
  LIVE: "bg-emerald-50 border-emerald-200 text-emerald-700",
  MANUAL_SOURCE: "bg-slate-100 border-slate-200 text-slate-600",
  AUTHENTICATION_REQUIRED: "bg-amber-50 border-amber-200 text-amber-700",
  DEGRADED: "bg-rose-50 border-rose-200 text-rose-700",
  CONFIGURED: "bg-slate-100 border-slate-200 text-slate-600",
};

export default function ProductIntelligence() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "lookup";
  const setTab = (t) => setSp({ tab: t });

  return (
    <div className="max-w-[1100px] mx-auto px-4 md:px-6 py-6 space-y-5" data-testid="product-intelligence-page">
      <div>
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-indigo-600" />
          <h1 className="text-xl font-semibold text-slate-900">Product Intelligence</h1>
        </div>
        <p className="text-[13px] text-slate-500 mt-0.5">
          One universal search across every brand & category — enter a Part Number, SKU, MTM or model and
          SUNTRONIX finds the official manufacturer specifications automatically.
        </p>
      </div>

      <QuickActionGroup module="product_intel" />
<div className="flex gap-1.5 border-b border-slate-200" data-testid="pi-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`pi-tab-${t.id}`}
            className={`px-3 py-2 text-[13px] font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "lookup" && <PsrefLookup />}
      {tab === "imported" && <LinkedList sync_status={null} testid="pi-imported" empty="No OEM-linked products yet." />}
      {tab === "updates" && <LinkedList sync_status="UPDATE_AVAILABLE" testid="pi-updates" empty="No OEM updates pending." />}
      {tab === "health" && <ConnectorHealth />}
    </div>
  );
}

/* ── Lenovo PSREF lookup ─────────────────────────────────────────── */
function PsrefLookup() {
  const navigate = useNavigate();
  const [part, setPart] = useState("");
  const [brand, setBrand] = useState("AUTO");
  const [brands, setBrands] = useState([]);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [recent, setRecent] = useState(() => {
    try { return JSON.parse(localStorage.getItem("psref_recent") || "[]"); } catch { return []; }
  });
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    api.get("/oem/connectors").then(({ data }) => setBrands((data.connectors || []).map((c) => c.brand))).catch(() => {});
  }, []);

  const search = async (overrideBrand) => {
    if (!part.trim()) { toast.error("Enter a Part Number / SKU / MTM"); return; }
    const b = overrideBrand || brand;
    setBusy(true); setRes(null); setShowAll(false);
    try {
      const { data } = await api.post("/oem/fetch", { brand: b === "AUTO" ? null : b, part_number: part.trim() });
      setRes(data);
      if (data.match_status === "EXACT") {
        toast.success(`Exact match: ${data.product_name}`);
        const next = [{ part: data.part_number, name: data.product_name, at: Date.now() },
          ...recent.filter((r) => r.part !== data.part_number)].slice(0, 8);
        setRecent(next); localStorage.setItem("psref_recent", JSON.stringify(next));
      } else if (data.match_status === "NEED_BRAND") {
        toast.info("Select the brand to continue");
      } else toast.info(data.message || "No exact match");
    } catch (e) { toast.error(e.response?.data?.detail || "Lookup failed"); }
    setBusy(false);
  };

  const importToMaster = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/oem/import", {
        brand: res.brand, part_number: res.part_number, create: true, category: "Laptops",
      });
      toast.success("Imported to Product Master");
      if (data.product?.id) navigate(`/technology/product-master?product=${data.product.id}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Import failed (product may already exist)");
    }
    setImporting(false);
  };

  const exact = res && res.match_status === "EXACT";
  const n = (exact && res.normalised) || {};

  return (
    <div className="space-y-4" data-testid="pi-lookup">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Search Product Intelligence</div>
        <div className="flex gap-2">
          <select value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="pi-brand-select"
            className="h-10 border border-slate-200 rounded-md px-2 text-[12px] bg-white shrink-0 max-w-[150px]">
            <option value="AUTO">Auto-detect brand</option>
            {brands.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <Input value={part} onChange={(e) => setPart(e.target.value)} data-testid="psref-input"
            placeholder="Part Number / SKU / MTM / Model — e.g. 21QVS18E00" className="h-10 font-mono"
            onKeyDown={(e) => e.key === "Enter" && search()} />
          <Button onClick={() => search()} disabled={busy} data-testid="psref-search"
            className="bg-indigo-600 hover:bg-indigo-700 shrink-0 h-10">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-1.5" /> Search</>}
          </Button>
        </div>
      </div>

      {res && res.match_status === "NEED_BRAND" && (
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5" data-testid="psref-needbrand">
          <div className="text-[12px] text-slate-600 mb-2">{res.message}</div>
          <div className="flex flex-wrap gap-1.5">
            {(res.brands || []).map((b) => (
              <button key={b} onClick={() => { setBrand(b); search(b); }}
                className="text-[11px] px-2 py-1 rounded-full border border-slate-200 hover:bg-indigo-50 hover:border-indigo-300 text-slate-600">{b}</button>
            ))}
          </div>
        </div>
      )}

      {res && !exact && res.match_status !== "NEED_BRAND" && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 flex gap-2 text-[12px] text-amber-800" data-testid="psref-nomatch">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> {res.message || "No exact match."}
        </div>
      )}

      {exact && (
        <div className="rounded-xl border border-emerald-200 bg-white overflow-hidden" data-testid="psref-result">
          <div className="px-4 py-3 bg-emerald-50/60 border-b border-emerald-100">
            <div className="flex items-center gap-2 text-[12px] font-semibold text-emerald-700">
              <CheckCircle2 className="h-4 w-4" /> Exact product found
            </div>
            <div className="mt-1 text-[16px] font-semibold text-slate-900" data-testid="psref-name">{res.product_name}</div>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
              <span className="font-mono">{res.part_number}</span>
              {res.region && <Badge variant="outline" className="text-[10px]">{res.region}</Badge>}
              <Badge className="bg-indigo-600 text-[10px]">{res.source}</Badge>
            </div>
          </div>
          <div className="p-4 space-y-3">
            {res.smart_description && (
              <div className="text-[12px] leading-relaxed text-slate-600 whitespace-pre-wrap" data-testid="psref-desc">
                {renderBold(res.smart_description)}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
              {[["processor", "Processor"], ["memory", "Memory"], ["storage", "Storage"],
                ["gpu", "Graphics"], ["display_size", "Display"], ["operating_system", "OS"],
                ["wireless", "Wireless"], ["battery", "Battery"], ["warranty", "Warranty"]]
                .filter(([k]) => n[k]).map(([k, l]) => (
                <div key={k} className="rounded border border-slate-100 bg-slate-50 px-2 py-1.5">
                  <div className="text-[9px] uppercase tracking-wide text-slate-400">{l}</div>
                  <div className="text-[11px] text-slate-700 line-clamp-2">{n[k]}</div>
                </div>
              ))}
            </div>
            <button onClick={() => setShowAll((v) => !v)} data-testid="psref-toggle-full"
              className="flex items-center gap-1 text-[12px] text-indigo-600 font-medium">
              <ChevronDown className={`h-4 w-4 transition-transform ${showAll ? "rotate-180" : ""}`} />
              {showAll ? "Hide" : "View"} full specifications
            </button>
            {showAll && (
              <div className="space-y-2" data-testid="psref-full">
                {(res.categories || []).map((c) => (
                  <div key={c.category} className="rounded-lg border border-slate-200">
                    <div className="px-3 py-1.5 bg-slate-50 text-[11px] font-semibold text-slate-600">{c.category}</div>
                    <div className="divide-y divide-slate-100">
                      {(c.items || []).map((it, i) => (
                        <div key={i} className="px-3 py-1.5 grid grid-cols-[130px_1fr] gap-2 text-[11px]">
                          <div className="text-slate-400">{it.label}</div>
                          <div className="text-slate-700 whitespace-pre-wrap">{it.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {res.images?.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {res.images.slice(0, 6).map((u, i) => (
                  <img key={i} src={u} alt="product" className="h-20 rounded border border-slate-200 object-contain bg-white shrink-0" />
                ))}
              </div>
            )}
            {res.services?.length > 0 && (
              <div data-testid="psref-services">
                <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Recommended services</div>
                {res.services.map((s, i) => <div key={i} className="text-[11px] text-slate-600 rounded border border-slate-100 bg-slate-50 px-2 py-1 mb-1">{s.name}</div>)}
              </div>
            )}
            <div className="flex flex-wrap gap-2 pt-1">
              <Button onClick={importToMaster} disabled={importing} data-testid="psref-import"
                className="bg-emerald-600 hover:bg-emerald-700 h-8 text-[12px]">
                {importing ? "Importing…" : <><PackageCheck className="h-3.5 w-3.5 mr-1.5" /> Import to Product Master</>}
              </Button>
              {res.source_url && (
                <a href={res.source_url} target="_blank" rel="noreferrer" data-testid="psref-open"
                  className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-700">
                  <ExternalLink className="h-3.5 w-3.5" /> Open in {res.source}
                </a>
              )}
              {res.datasheet?.url && (
                <a href={res.datasheet.url} target="_blank" rel="noreferrer" data-testid="psref-datasheet"
                  className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-700">
                  <FileText className="h-3.5 w-3.5" /> View datasheet
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {recent.length > 0 && (
        <div data-testid="psref-recent">
          <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Recently looked up</div>
          <div className="flex flex-wrap gap-1.5">
            {recent.map((r) => (
              <button key={r.part} onClick={() => { setPart(r.part); }}
                className="text-[11px] px-2 py-1 rounded-full border border-slate-200 hover:bg-slate-50 text-slate-600">
                <span className="font-mono">{r.part}</span> · {r.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── linked / updates list ───────────────────────────────────────── */
function LinkedList({ sync_status, testid, empty }) {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/oem/linked", { params: sync_status ? { sync_status } : {} });
      setItems(data.items || []);
    } catch { setItems([]); }
  }, [sync_status]);
  useEffect(() => { load(); }, [load]);
  if (!items) return <div className="text-[12px] text-slate-400 py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>;
  if (!items.length) return <div className="text-[12px] text-slate-400 py-10 text-center" data-testid={`${testid}-empty`}>{empty}</div>;
  return (
    <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid={testid}>
      {items.map((p) => (
        <button key={p.id} onClick={() => navigate(`/technology/product-master?product=${p.id}`)}
          className="w-full text-left px-4 py-2.5 hover:bg-slate-50 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[13px] font-medium text-slate-800 truncate">{p.product_name}</div>
            <div className="text-[11px] text-slate-500 font-mono">{p.part_number} · {p.source}</div>
          </div>
          <Badge className="bg-slate-100 border-slate-200 text-slate-600 text-[10px] shrink-0">{(p.sync_status || "SYNCED").replace(/_/g, " ")}</Badge>
        </button>
      ))}
    </div>
  );
}

/* ── connector health ────────────────────────────────────────────── */
function ConnectorHealth() {
  const [rows, setRows] = useState(null);
  const [testing, setTesting] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/oem/connectors"); setRows(data.connectors || []); }
    catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const test = async (brand) => {
    setTesting(brand);
    try {
      const { data } = await api.post(`/oem/test/${brand}`);
      if (data.result === "PASSED") toast.success(`${brand}: connector test PASSED — ${data.product_name}`);
      else if (data.result === "FAILED") toast.error(`${brand}: test failed — ${data.error || data.message || "degraded"}`);
      else toast.info(`${brand}: ${data.status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
    setTesting(null);
  };

  if (!rows) return <div className="text-[12px] text-slate-400 py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>;
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="pi-health">
      <div className="hidden sm:grid grid-cols-[1fr_120px_100px_80px_90px_90px] gap-2 px-4 py-2 bg-slate-50 text-[10px] uppercase tracking-wide text-slate-400">
        <div>Connector</div><div>Mode</div><div>Status</div><div>Linked</div><div>Updates</div><div>Test</div>
      </div>
      <div className="divide-y divide-slate-100">
        {rows.map((c) => (
          <div key={c.brand} className="grid grid-cols-2 sm:grid-cols-[1fr_120px_100px_80px_90px_90px] gap-2 px-4 py-2.5 items-center text-[12px]" data-testid={`pi-health-${c.brand}`}>
            <div>
              <div className="font-medium text-slate-800 flex items-center gap-1.5">
                {c.mode === "COMPONENT_ENRICHMENT" ? <Cpu className="h-3.5 w-3.5 text-slate-400" /> : <Activity className="h-3.5 w-3.5 text-slate-400" />}
                {c.brand}
              </div>
              <div className="text-[10px] text-slate-400">{c.connector} · {c.version}</div>
            </div>
            <div className="text-[10px] text-slate-500">{(c.mode || "").replace(/_/g, " ")}</div>
            <div><Badge className={(STATUS_TONE[c.status] || STATUS_TONE.CONFIGURED) + " text-[10px]"}>{(c.status || "").replace(/_/g, " ")}</Badge></div>
            <div className="text-slate-600">{c.products_linked || 0}</div>
            <div className="text-slate-600">{c.updates_available || 0}</div>
            <div>
              <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" disabled={testing === c.brand}
                onClick={() => test(c.brand)} data-testid={`pi-test-${c.brand}`}>
                {testing === c.brand ? <Loader2 className="h-3 w-3 animate-spin" /> : <><RefreshCw className="h-3 w-3 mr-1" /> Test</>}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
